import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
    Box,
    useToast,
    Stack,
    HStack,
    Button
} from '@chakra-ui/react';
import Form from '@rjsf/chakra-ui';
import validator from "@rjsf/validator-ajv8";
import { getDeletedValues, reverseTransformData, transformData } from '../../util/helper';
import ImageWidget from '../../CommonComponents/ImageWidget';
import { createNewFlipBook, getFlipBookData, updateFlipBookData } from '../../services/FlipBooks';
import { getMasterListAliasSuggestions } from '../../services/masterlistAlias';
import TypeaheadWidget from '../../CommonComponents/TypeaheadWidget';
import { handleApiError, showSuccessToast } from '../../util/errorHandler';
import FormLoader from '../../CommonComponents/FormLoader';

// Left Column Schema - Basic Fields
const leftColumnSchema = {
    "title": "Flip Book Fields",
    "description": "",
    "type": "object",
    "properties": {
        "_id": {
            "type": "string",
            "title": "_id"
        },
        masterlist_alias: {
            "type": "string",
            "title": "Masterlist Alias",
            "default": ""
        },
    },
    required: ['masterlist_alias']
};

// Right Column Schema - Images
const rightColumnSchema = {
    "title": "",
    "description": "",
    "type": "object",
    "properties": {
        images: {
            type: "array",
            title: "Images",
            items: { 
                type: "object", 
                properties: { 
                    image_alias: { type: "string", default: "" }, 
                    image: { type: "string", default: "" } 
                } 
            },
            "default": []
        },
    }
};

// Left Column UI Schema
const leftColumnUiSchema = (alias, isSubmitting) => ({
    "ui:submitButtonOptions": {
        "norender": true
    },
    "ui:style": {
        fontSize: "14px",
    },
    "_id": {
        "ui:widget": "hidden"
    },
    masterlist_alias: {
        'ui:emptyValue': '',
        'ui:readonly': !!alias,
        'ui:widget': 'masterlistAliasTypeahead',
        'ui:disabled': !!alias
    },
});

// Right Column UI Schema
const rightColumnUiSchema = (isSubmitting) => ({
    "ui:submitButtonOptions": {
        "norender": true
    },
    "ui:style": {
        fontSize: "14px",
    },
    images: { 
        "ui:emptyValue": "", 
        items: { 
            image_alias: { "ui:emptyValue": "" }, 
            image: { "ui:widget": ImageWidget, "ui:emptyValue": "" } 
        } 
    },
});

// Combined Schema for Single Column (Preview Mode)
const combinedSchema = {
    "title": "Flip Book Fields",
    "description": "",
    "type": "object",
    "properties": {
        "_id": {
            "type": "string",
            "title": "_id"
        },
        masterlist_alias: {
            "type": "string",
            "title": "Masterlist Alias",
            "default": ""
        },
        images: {
            type: "array",
            title: "Images",
            items: { 
                type: "object", 
                properties: { 
                    image_alias: { type: "string", default: "" }, 
                    image: { type: "string", default: "" } 
                } 
            },
            "default": []
        },
    },
    required: ['masterlist_alias']
};

// Combined UI Schema for Single Column (Preview Mode)
const combinedUiSchema = (alias, isSubmitting) => ({
    "ui:submitButtonOptions": {
        "norender": true
    },
    "ui:style": {
        fontSize: "14px",
    },
    "_id": {
        "ui:widget": "hidden"
    },
    masterlist_alias: {
        'ui:emptyValue': '',
        'ui:readonly': !!alias,
        'ui:widget': 'masterlistAliasTypeahead',
        'ui:disabled': !!alias
    },
    images: { 
        "ui:emptyValue": "", 
        items: { 
            image_alias: { "ui:emptyValue": "" }, 
            image: { "ui:widget": ImageWidget, "ui:emptyValue": "" } 
        } 
    },
});

const FlipBook = () => {
    const { alias } = useParams();
    const [leftFormData, setLeftFormData] = useState({});
    const [rightFormData, setRightFormData] = useState({});
    const [combinedFormData, setCombinedFormData] = useState({});
    const [quickSave, setQuickSave] = useState(false);
    const [originalFormData, setOriginalFormData] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const toast = useToast();
    const navigate = useNavigate();
    const previewEnabled = useSelector(state => state.preview.isEnabled);

    // Split data for two-column layout
    const splitFormData = (data = {}) => {
        const leftFields = ['_id', 'masterlist_alias'];
        const rightFields = ['images'];
        
        const leftData = {};
        const rightData = {};
        
        // Process left fields with defaults
        leftFields.forEach(field => {
            if (data.hasOwnProperty(field)) {
                leftData[field] = data[field];
            } else {
                if (field === '_id') {
                    leftData[field] = data[field] || "";
                } else {
                    leftData[field] = "";
                }
            }
        });
        
        // Process right fields with defaults
        rightFields.forEach(field => {
            if (data.hasOwnProperty(field)) {
                rightData[field] = data[field];
            } else {
                if (field === 'images') {
                    rightData[field] = [];
                } else {
                    rightData[field] = "";
                }
            }
        });
        
        return { leftData, rightData };
    };
    
    // Combine data from both forms
    const combineFormData = (leftData, rightData) => {
        return { ...leftData, ...rightData };
    };

    // Fetch initial data
    useEffect(() => {
        const getBasicData = async () => {
            try {
                if (alias) {
                    const result = await getFlipBookData(alias);
                    if (result && result?.data?.[0]) {
                        const fieldsToTransform = ['images'];
                        const transformedData = transformData(result?.data[0], fieldsToTransform);

                        // Split data for two-column layout
                        const { leftData, rightData } = splitFormData(transformedData);
                        setLeftFormData(leftData);
                        setRightFormData(rightData);
                        setCombinedFormData(transformedData);
                        setOriginalFormData(transformedData);
                        setQuickSave(false);
                    }
                } else {
                    // For new flip books, initialize empty form data
                    const emptyData = {};
                    const { leftData, rightData } = splitFormData(emptyData);
                    setLeftFormData(leftData);
                    setRightFormData(rightData);
                    setCombinedFormData(emptyData);
                    setOriginalFormData(emptyData);
                }
            } catch (error) {
                handleApiError(error, toast, {
                    defaultErrorMessage: "Failed to fetch data"
                });
            } finally {
                setIsLoading(false);
            }
        };

        getBasicData();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [alias, quickSave]);

    // Handle form changes for two-column layout
    const handleLeftFormChange = (data) => {
        setLeftFormData(data.formData);
    };
    
    const handleRightFormChange = (data) => {
        setRightFormData(data.formData);
    };

    const handleCombinedFormChange = (data) => {
        setCombinedFormData(data.formData);
    };

    const loadMasterlistAliasSuggestions = async (inputValue) => {
        try {
            const result = await getMasterListAliasSuggestions(20, 1, inputValue);
            return result?.data?.data || [];
        } catch (error) {
            handleApiError(error, toast, {
                defaultErrorMessage: "Failed to load document types"
            });
            return [];
        }
    };

    const fetchMasterlistAliasSuggestions = async (inputValue) => {
        const fetchedMasterListAliasSuggestions = await loadMasterlistAliasSuggestions(inputValue);

        // Ensure inputValue is string and trimmed
        if (!inputValue || typeof inputValue !== "string" || !fetchedMasterListAliasSuggestions) {
            return [];
        }

        return fetchedMasterListAliasSuggestions?.map((item) => ({
            label: item.alias,
            value: item.alias
        }));
    };


    // Submit updated data
    const handleSubmit = async () => {
        // Use combined form data in preview mode, otherwise combine left and right forms
        const finalFormData = previewEnabled ? combinedFormData : combineFormData(leftFormData, rightFormData);
        
        // Validate required fields (only for existing flip books)
        if (alias && !finalFormData.masterlist_alias) {
            return;
        }
        
        // For new flip books, at least masterlist_alias should be present
        if (!alias && !finalFormData.masterlist_alias) {
            return;
        }

        const updatedFormData = reverseTransformData(finalFormData, []);
        const deletedValues = alias ? getDeletedValues(originalFormData, finalFormData) : {};

        try {
            if (alias) {
                setIsSubmitting(true)
                const result = await updateFlipBookData(alias, updatedFormData, deletedValues);

                if (result?.status === 200) {
                    setQuickSave(prev => !prev);
                    showSuccessToast(toast, "Data updated successfully");
                }
            } else {
                setIsSubmitting(true);
                const result = await createNewFlipBook(updatedFormData);  // Call the create API
                if (result?.status === 201) {
                    setQuickSave(prev => !prev);
                    showSuccessToast(toast, "New flip book created successfully");
                    navigate(`/flip-books/${result?.data?.masterlist_alias}`);  // Navigate to the newly created flip book
                }
            }
        } catch (error) {
            handleApiError(error, toast);
        } finally {
            setIsSubmitting(false);
        }
    };

    const widgets = {
        masterlistAliasTypeahead: (props) => (
            <TypeaheadWidget
                {...props}
                fetchOptions={fetchMasterlistAliasSuggestions}
                readonly={!!alias}
            />
        ),
    };

    return (
        <Box maxW="auto" pb="100px">
            {isLoading ? (
                <FormLoader />
            ) : (
                <Box>
                    {previewEnabled ? (
                        /* Single column layout for preview mode */
                        <Box display={"flex"} flexDirection={"row"} alignItems={"flex-start"}>
                            <Box flex={1} bg="white" border="1px solid" borderColor="gray.200" p={4} borderRadius="md">
                                <Box fontSize="lg" fontWeight="bold" mb={4}>Flip Book Fields</Box>
                                <Form
                                    schema={combinedSchema}
                                    uiSchema={combinedUiSchema(alias, isSubmitting)}
                                    formData={combinedFormData}
                                    validator={validator}
                                    onChange={handleCombinedFormChange}
                                    widgets={widgets}
                                    fontsize={"14px"}
                                />
                            </Box>
                        </Box>
                    ) : (
                        /* Two column layout with separate forms */
                        <Box display={"flex"} flexDirection={"row"} alignItems={"flex-start"}>
                            <Stack flexDirection="row" gap={4} alignItems="flex-start" flex={1}>
                                <HStack alignItems="flex-start" flex={1}>
                                    {/* Left Column */}
                                    <Box flex={1} bg="white" border="1px solid" borderColor="gray.200" p={4} borderRadius="md">
                                        <Box fontSize="lg" fontWeight="bold" mb={4}>Flip Book Fields</Box>
                                        <Form
                                            schema={leftColumnSchema}
                                            uiSchema={leftColumnUiSchema(alias, isSubmitting)}
                                            formData={leftFormData}
                                            validator={validator}
                                            onChange={handleLeftFormChange}
                                            widgets={widgets}
                                            fontsize={"14px"}
                                        />
                                    </Box>
                                    {/* Right Column */}
                                    <Box flex={1} bg="white" border="1px solid" borderColor="gray.200" p={4} borderRadius="md">
                                        <Form
                                            schema={rightColumnSchema}
                                            uiSchema={rightColumnUiSchema(isSubmitting)}
                                            formData={rightFormData}
                                            validator={validator}
                                            onChange={handleRightFormChange}
                                            widgets={widgets}
                                            fontsize={"14px"}
                                        />
                                    </Box>
                                </HStack>
                            </Stack>
                        </Box>
                    )}
                </Box>
            )}
            
            {/* Fixed Save Button at Bottom */}
            <Box
                position="fixed"
                bottom={0}
                left="250px" // Account for sidebar width
                right={0}
                bg="white"
                borderTop="1px solid"
                borderColor="gray.200"
                p={4}
                zIndex={1000}
                display="flex"
            >
                <Box maxW="auto" display="flex" mx="auto">
                    <Button
                        colorScheme="blue"
                        size="lg"
                        width="auto"
                        isLoading={isSubmitting}
                        loadingText="Saving..."
                        onClick={() => handleSubmit()}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Saving...' : 'Save'}
                    </Button>
                </Box>
            </Box>
            
            {/* Add padding to prevent content from being hidden behind fixed button */}
            <Box pb="100px" />
        </Box>
    );
};

export default FlipBook;